import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import api from '@/axios';
import { GET_SUPPLY_CHAIN_INFORMATIONS_KEY } from '@/keys';
import type { SupplyChainInformation } from '@/types/core/purchase-order/supply-chain-information';

type ReorderSupplyChainInformationsVariables = {
  productionDeclarationId: string;
  materialId: string;
  commodityOriginId: string;
  orderedIds: string[];
  token?: string;
};

type ReorderSupplyChainInformationsContext = {
  previousSupplyChainInformations?: SupplyChainInformation[];
};

const getSupplyChainInformationsQueryKey = ({
  productionDeclarationId,
  materialId,
  commodityOriginId,
  token,
}: ReorderSupplyChainInformationsVariables) => [
  ...GET_SUPPLY_CHAIN_INFORMATIONS_KEY,
  productionDeclarationId,
  materialId,
  commodityOriginId,
  token ?? '',
];

const reorderSupplyChainInformationsCacheData = (
  supplyChainInformations: SupplyChainInformation[] | undefined,
  orderedIds: string[],
): SupplyChainInformation[] | undefined => {
  if (!supplyChainInformations) {
    return supplyChainInformations;
  }

  const supplyChainInformationById = new Map(
    supplyChainInformations.map((supplyChainInformation) => [
      supplyChainInformation.id,
      supplyChainInformation,
    ]),
  );

  return orderedIds
    .map((id, index) => {
      const supplyChainInformation = supplyChainInformationById.get(id);

      return supplyChainInformation
        ? {
            ...supplyChainInformation,
            actorFlowOrder: index + 1,
          }
        : null;
    })
    .filter(
      (
        supplyChainInformation,
      ): supplyChainInformation is SupplyChainInformation =>
        supplyChainInformation !== null,
    );
};

async function reorderSupplyChainInformations({
  productionDeclarationId,
  materialId,
  commodityOriginId,
  orderedIds,
  token,
}: ReorderSupplyChainInformationsVariables): Promise<void> {
  await api.patch(
    `/production-declarations/${productionDeclarationId}/materials/${materialId}/commodity-origins/${commodityOriginId}/supply-chain-informations/reorder`,
    { orderedIds },
    {
      params: token ? { token } : undefined,
    },
  );
}

export function useReorderSupplyChainInformations(): UseMutationResult<
  void,
  Error,
  ReorderSupplyChainInformationsVariables,
  ReorderSupplyChainInformationsContext
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reorderSupplyChainInformations,
    onMutate: async (variables) => {
      const queryKey = getSupplyChainInformationsQueryKey(variables);

      await queryClient.cancelQueries({ queryKey });

      const previousSupplyChainInformations =
        queryClient.getQueryData<SupplyChainInformation[]>(queryKey);

      queryClient.setQueryData<SupplyChainInformation[]>(
        queryKey,
        (currentSupplyChainInformations) =>
          reorderSupplyChainInformationsCacheData(
            currentSupplyChainInformations,
            variables.orderedIds,
          ),
      );

      return { previousSupplyChainInformations };
    },
    onError: (_error, variables, context) => {
      if (!context?.previousSupplyChainInformations) {
        return;
      }

      queryClient.setQueryData(
        getSupplyChainInformationsQueryKey(variables),
        context.previousSupplyChainInformations,
      );
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: getSupplyChainInformationsQueryKey(variables),
      });
    },
  });
}
